import React, { FC } from "react";
import {
    useSearchState
} from "hooks/SearchContext";
import { Company, Location } from "interfaces/context/searchContextInterfaces";
import { isMobile, toggleWindowFreeze } from "utils/browser";
import { Filter, OpenState } from "./FilterNav";
import { FilterTrigger } from "./FilterTrigger";
import { IconClose } from "../icons/IconClose";
import FilterPanelStyles from "./FilterPanel.module.scss";
import styles from "./FilterPanelCollection.module.scss";

export interface FilterPanelCollectionProps {
    buttonOptions: Filter[];
    isOpen?: boolean;
    onApply: () => void;
    toggleSoloMenu: (filterKey: string) => void;
    toggleMegaMenu: () => void;
    onBackBtn: (filterKey: string) => void;
    reset: () => void;
    openState: OpenState;
}

const companyFilters = ["perks", "companySize", "industry", "companyLeadership", "diversityInclusionItems"];
const jobFilters = ["postedDateRange", "category", "experienceLevel", "remote", "radius"];

// Some buttons should be disabled if you have selected a company or have not selected a physical location yet.
export const isDisabled = (
    id: string,
    userSelectedLocation: Location,
    userSelectedCompany: Company
) => {
    const hasLocation = !!userSelectedLocation.value;
    const isLocationProp = ["radius"].includes(id);
    const hasCompany = !!userSelectedCompany?.value;
    const isCompanyProp = ["companySize", "industry", "perks", "companyLeadership"].includes(id);
    return !!((isCompanyProp && hasCompany)
        || (isLocationProp && !hasLocation)
        || (isLocationProp && hasLocation && userSelectedLocation.remote_work_location_type === "virtual"));
};

/**
 * `FilterPanelCollection` is a functional component that renders a collection of triggers
 * for job and company filters. Unlike FilterNav, these triggers do not have their own panels.
 * Instead, they toggle the Parent FilterNav trigger panels.
 */
export const FilterPanelCollection: FC<FilterPanelCollectionProps> = ({
    isOpen = false,
    buttonOptions,
    onApply = () => { },
    onBackBtn,
    toggleSoloMenu,
    toggleMegaMenu,
    reset,
    openState
}) => {
    const {
        company: { userSelectedCompany },
        location: { userSelectedLocation }
    } = useSearchState();

    const companyButtons = buttonOptions.filter((item) => companyFilters.includes(item.id));
    const jobButtons = buttonOptions.filter((item) => jobFilters.includes(item.id));

    if (isMobile()) {
        toggleWindowFreeze();
    }

    return (
        <div
            className={`${styles.container} ${isOpen ? "" : styles.hidden}`}
            role="dialog"
            aria-label="Filter options"
        >
            <button
                type="button"
                className={styles.closeButton}
                onClick={toggleMegaMenu}
                aria-label="Close filter menu"
            >
                <IconClose />
            </button>
            <div className={styles.expandable}>
                <p className={styles.sectionHeader}>Job Filters</p>
                {jobButtons.map((filter) => (
                    <FilterTrigger
                        key={filter.label}
                        id={filter.id}
                        label={filter.label}
                        options={filter.options}
                        hasIcon={filter.hasIcon}
                        onClick={() => toggleSoloMenu(filter.id)}
                        onBackBtn={() => onBackBtn(filter.id)}
                        disabled={isDisabled(filter.id, userSelectedLocation, userSelectedCompany)}
                        isOpen={openState[filter.id]}
                        isMegaMenu={true}
                        wrapperClass={styles.wrapperClass}
                    />
                ))}

                <p className={styles.sectionHeader}>Company Filters</p>
                {companyButtons.map((filter) => (
                    <FilterTrigger
                        key={filter.label}
                        id={filter.id}
                        label={filter.label}
                        options={filter.options}
                        hasIcon={filter.hasIcon}
                        onClick={() => toggleSoloMenu(filter.id)}
                        onBackBtn={() => onBackBtn(filter.id)}
                        disabled={isDisabled(filter.id, userSelectedLocation, userSelectedCompany)}
                        isOpen={openState[filter.id]}
                        isMegaMenu={true}
                        wrapperClass={styles.wrapperClass}
                    />
                ))}
            </div>
            <div className={FilterPanelStyles.actionButtons}>
                <button
                    type="button"
                    className={FilterPanelStyles.cancelButton}
                    onClick={reset}
                >
                    Reset All
                </button>
                <button
                    type="button"
                    className={FilterPanelStyles.applyButton}
                    onClick={onApply}
                >
                    Apply
                </button>
            </div>
        </div>
    );
};
